import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@heroui/react";

export interface FilterPopoverProps {
  /** Content rendered inside the trigger button (icon) */
  triggerContent: React.ReactNode;
  /** Custom close icon; defaults to "✕" */
  closeIcon?: React.ReactNode;
  /** Highlights the trigger button when filters are active */
  isActive?: boolean;
  /** Disable the trigger button */
  isDisabled?: boolean;
  /** Panel header title */
  title?: string;
  /** Filter body (e.g. RangeFilterGroup elements) */
  children: React.ReactNode;
  /** Called when the user clicks "Применить"; panel closes automatically */
  onApply: () => void;
  /** Called when the user clicks "Сбросить" */
  onReset: () => void;
  /** Called when the panel opens -- use to sync local state from store */
  onOpen?: () => void;
  applyLabel?: string;
  resetLabel?: string;
  /** Controlled open state. If provided, component runs in controlled mode. */
  isOpen?: boolean;
  /** Open/close callback for controlled mode. */
  onOpenChange?: (open: boolean) => void;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  triggerContent,
  closeIcon = "✕",
  isActive = false,
  isDisabled = false,
  title = "Фильтры",
  children,
  onApply,
  onReset,
  onOpen,
  applyLabel = "Применить",
  resetLabel = "Сбросить",
  isOpen: isOpenProp,
  onOpenChange,
}) => {
  const isControlled = isOpenProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? isOpenProp : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Position panel below the anchor element; recalculate on window resize
  const positionPanel = useCallback(() => {
    if (anchorRef.current && panelRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      panelRef.current.style.top = `${rect.bottom + 8}px`;
      panelRef.current.style.left = `${rect.left}px`;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    positionPanel();
    window.addEventListener("resize", positionPanel);
    return () => window.removeEventListener("resize", positionPanel);
  }, [isOpen, positionPanel]);

  // Notify "onOpen" once per open transition
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      onOpen?.();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, onOpen]);

  // Close on outside click
  const handleOutsideClick = useCallback(
    (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    },
    [setOpen],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, handleOutsideClick]);

  const handleToggle = () => {
    setOpen(!isOpen);
  };

  const handleApply = () => {
    onApply();
    setOpen(false);
  };

  return (
    <div className="sc-filter-anchor" ref={anchorRef}>
      <Button
        isIconOnly
        variant="tertiary"
        aria-label="Фильтры"
        onPress={handleToggle}
        isDisabled={isDisabled}
        className={
          isOpen || isActive
            ? "filter-btn--active icon-btn--card"
            : "icon-btn--card"
        }
      >
        {triggerContent}
      </Button>

      {isOpen && (
        <div className="sc-filter-panel" ref={panelRef}>
          <div className="sc-filter-panel__header">
            <span className="sc-filter-panel__title">{title}</span>
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              aria-label="Закрыть фильтры"
              onPress={() => setOpen(false)}
            >
              {closeIcon}
            </Button>
          </div>

          <div className="sc-filter-panel__body">{children}</div>

          <div className="sc-filter-panel__footer">
            <Button variant="tertiary" size="sm" onPress={onReset}>
              {resetLabel}
            </Button>
            <Button variant="primary" size="sm" onPress={handleApply}>
              {applyLabel}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

FilterPopover.displayName = "FilterPopover";
export default FilterPopover;
